import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Star, Edit, Trash2, Save, X, AlertTriangle, ShieldAlert, ThumbsUp, ThumbsDown, Loader2, MessageSquare, Pencil, Send } from 'lucide-react';
import { getGameReviews, getUserGameReview, createGameReview, updateGameReview, deleteGameReview, voteOnReview } from '../lib/game/reviewService';
import { Game, GameReview as OriginalGameReview, UserProfile as SupabaseUserProfile } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import SEO from '../components/SEO';
import { rateGame } from '../lib/game/ratingService';
import { supabase } from '../lib/supabase';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "react-hot-toast"

// Definindo um tipo esperado para o resultado da votação
interface VoteResultType {
  upvotes: number;
  downvotes: number;
  balance: number;
  user_vote: 'upvote' | 'downvote' | null;
}

// Interface para GameReview com dados de perfil do usuário e votos
interface GameReviewWithProfile {
  id: string;
  user_id: string;
  game_id: string;
  review_text: string;
  created_at: string;
  updated_at: string;
  profiles: SupabaseUserProfile | null; 
  rating: number | null; 
  upvotes: number;       
  downvotes: number;     
  balance: number;       
  user_vote: 'upvote' | 'downvote' | null; 
  game_name?: string; 
  game_slug?: string;
}

// Helper function to transform raw review data to GameReviewWithProfile
const transformRawReview = (rawReview: any): GameReviewWithProfile => {
  let profileData: SupabaseUserProfile | null = null;

  // Attempt 1: RPC returns profiles as a nested object (e.g., from a join)
  if (rawReview.profiles && typeof rawReview.profiles === 'object' && rawReview.profiles.id) {
    profileData = {
      id: rawReview.profiles.id,
      username: rawReview.profiles.username,
      avatar_url: rawReview.profiles.avatar_url,
      // Add other SupabaseUserProfile fields if they are part of the spec and available
      // e.g. full_name: rawReview.profiles.full_name, bio: rawReview.profiles.bio etc.
    } as SupabaseUserProfile;
  }
  // Attempt 2: RPC returns profile fields directly at the review's root
  else if (rawReview.user_id && rawReview.username && typeof rawReview.avatar_url !== 'undefined') {
    profileData = {
      id: rawReview.user_id, // Assuming user_id on the review is the profile id
      username: rawReview.username,
      avatar_url: rawReview.avatar_url,
    } as SupabaseUserProfile;
  }
  // Attempt 3: Handle if profile data is flat on the review object itself (as some parts of the code might expect)
  // This is more of a safeguard for diverse RPC return structures.
  else if (rawReview.user_id && rawReview.username !== undefined && rawReview.avatar_url !== undefined) {
    profileData = {
        id: rawReview.user_id,
        username: rawReview.username,
        avatar_url: rawReview.avatar_url,
    } as SupabaseUserProfile;
  }


  return {
    id: rawReview.id,
    user_id: rawReview.user_id,
    game_id: rawReview.game_id,
    review_text: rawReview.review_text,
    created_at: rawReview.created_at,
    updated_at: rawReview.updated_at,
    profiles: profileData,
    rating: rawReview.rating !== undefined && rawReview.rating !== null ? Number(rawReview.rating) : null,
    upvotes: rawReview.upvotes || 0,
    downvotes: rawReview.downvotes || 0,
    balance: rawReview.balance || 0,
    user_vote: rawReview.user_vote || null,
    game_name: rawReview.game_name,
    game_slug: rawReview.game_slug,
  };
};

const GameReviewsPage: React.FC = () => {
  const { gameId: gameIdFromParams, gameSlug } = useParams<{ gameId?: string; gameSlug?: string }>();
  const gameId = gameIdFromParams;
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { theme } = useTheme();
  
  const [game, setGame] = useState<Game | null>(null);
  const [reviews, setReviews] = useState<GameReviewWithProfile[]>([]);
  const [userReview, setUserReview] = useState<GameReviewWithProfile | null>(null);
  const [reviewText, setReviewText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingReviewText, setEditingReviewText] = useState('');
  const [rating, setRating] = useState<number | null>(null);
  const [editingRating, setEditingRating] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [voteLoading, setVoteLoading] = useState(false);
  const [voteLoadingId, setVoteLoadingId] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showAdminDeleteConfirmation, setShowAdminDeleteConfirmation] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState<GameReviewWithProfile | null>(null);
  const [adminReviewToDeleteId, setAdminReviewToDeleteId] = useState<string | null>(null);
  const [isModeratingText, setIsModeratingText] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const identifier = gameSlug || gameId;
        if (!identifier) {
          setError("Identificador do jogo não encontrado.");
          setLoading(false);
          return;
        }

        let gameData: Game | null = null;
        if (gameSlug) {
            const { data, error: gameError } = await supabase.from('games').select('*').eq('slug', gameSlug).single();
            if (gameError && gameError.code !== 'PGRST116') throw gameError;
            gameData = data as Game | null;
        } else if (gameId) {
            const { data, error: gameError } = await supabase.from('games').select('*').eq('id', gameId).single();
            if (gameError && gameError.code !== 'PGRST116') throw gameError;
            gameData = data as Game | null;
        }

        if (!gameData) {
          setError("Jogo não encontrado.");
          setLoading(false);
          return;
        }
        setGame(gameData);

        // 1. Buscar todas as reviews para exibição geral
        const reviewsData = await getGameReviews(gameData.id);
        const transformedReviews = reviewsData.map(transformRawReview);
        setReviews(transformedReviews); 
        
        // 2. Se o usuário estiver logado, buscar sua review específica e/ou rating de user_games
        if (user?.id) {
          const userSpecificData = await getUserGameReview(user.id, gameData.id);

          if (userSpecificData) {
            // userSpecificData é do tipo GameReview e contém o rating de user_games
            // e a review textual se existir.
            
            let finalUserReviewForState: GameReviewWithProfile | null = null;
            
            // Se userSpecificData.id existe, significa que uma review textual foi encontrada.
            // Tentamos encontrar a versão completa (com votos, etc.) na lista `transformedReviews`.
            if (userSpecificData.id) {
              const existingReviewInList = transformedReviews.find(r => r.id === userSpecificData.id);
              if (existingReviewInList) {
                finalUserReviewForState = {
                  ...existingReviewInList,
                  rating: userSpecificData.rating !== undefined ? userSpecificData.rating : null,
                  review_text: userSpecificData.review_text,
                };
              } else {
                finalUserReviewForState = transformRawReview(userSpecificData); 
              }
            } else {
              finalUserReviewForState = {
                id: '',
                user_id: user.id,
                game_id: gameData.id,
                review_text: '', 
                created_at: '',
                updated_at: '',
                profiles: user ? { 
                  id: user.id, 
                  username: user.username, 
                  avatar_url: user.avatar_url, 
                  email: user.email || '',
                  created_at: user.created_at || '',
                  updated_at: user.updated_at || '',
                } as SupabaseUserProfile : null,
                rating: userSpecificData.rating !== undefined ? userSpecificData.rating : null,
                upvotes: 0,
                downvotes: 0,
                balance: 0,
                user_vote: null,
                game_name: gameData.name,
                game_slug: gameData.slug === null ? undefined : gameData.slug,
              };
            }
            
            setUserReview(finalUserReviewForState);
            setReviewText(finalUserReviewForState?.review_text || '');
            setRating(finalUserReviewForState?.rating !== undefined ? finalUserReviewForState.rating : null);
            setEditingReviewText(finalUserReviewForState?.review_text || '');
            setEditingRating(finalUserReviewForState?.rating !== undefined ? finalUserReviewForState.rating : null);

          } else {
            // Nenhuma review textual nem rating em user_games para este usuário
            setUserReview(null);
            setReviewText('');
            setRating(null);
            setEditingReviewText('');
            setEditingRating(null);
          }
        }
      } catch (err: any) {
        console.error("Erro ao buscar dados:", err);
        setError(err.message || "Erro ao carregar dados da página de avaliações.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [gameId, gameSlug, user?.id]);

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !game) {
      setError("Você precisa estar logado e um jogo deve ser selecionado.");
      return;
    }

    if (!editingReviewText.trim()) {
      setError("A avaliação não pode estar vazia.");
      return;
    }
    
    setIsModeratingText(true);
    setError(null);
    toast.loading('Analisando seu texto...', { id: 'text-moderation-toast' });

    try {
      const { data: textModerationResult, error: textModerationError } = await supabase.functions.invoke('moderate-text', {
        body: { textContent: editingReviewText.trim() }
      });

      toast.dismiss('text-moderation-toast');

      if (textModerationError) {
        console.warn('[GameReviewsPage] Erro crítico ao invocar a função de moderação de texto:', textModerationError);
        toast.error(textModerationError.message || 'Falha crítica ao verificar o texto.', { duration: 7000 });
        setIsModeratingText(false);
        return;
      }

      if (textModerationResult && !textModerationResult.success) {
        const detailedErrorMessage = textModerationResult.error || 'Conteúdo do texto da avaliação considerado impróprio.';
        console.warn(`[GameReviewsPage] Moderação de texto falhou: ${detailedErrorMessage}`);
        toast.error(detailedErrorMessage, { duration: 7000 });
        setError(detailedErrorMessage);
        setIsModeratingText(false);
        return; 
      }
      if (textModerationResult && textModerationResult.success) {
        toast.success(textModerationResult.message || 'Texto da avaliação verificado!', { duration: 2000 });
      }
      
    } catch (err: any) {
      toast.dismiss('text-moderation-toast');
      console.error('[GameReviewsPage] Erro durante moderação de texto:', err);
      toast.error(`Falha ao verificar texto da avaliação: ${err.message}`);
      setError(`Falha ao verificar texto: ${err.message}`);
      setIsModeratingText(false);
      return;
    }

    if (editingRating === null || editingRating < 1 || editingRating > 5) {
      setError("Por favor, forneça uma nota válida entre 1 e 5 para o jogo.");
      setIsModeratingText(false);
      return;
    }

    setActionLoading(true);
    setIsModeratingText(false);

    try {
      let savedTextReviewData: OriginalGameReview;
      let transformedSavedReview: GameReviewWithProfile;

      if (userReview && isEditing) {
        savedTextReviewData = await updateGameReview(userReview.id, editingReviewText, user.id);
        transformedSavedReview = transformRawReview(savedTextReviewData);
      } else {
        savedTextReviewData = await createGameReview(user.id, game.id, editingReviewText);
        transformedSavedReview = transformRawReview(savedTextReviewData);
      }
      
      if (editingRating !== null) {
        await rateGame(user.id, game.id, editingRating);
      }

      toast.success(isEditing ? 'Avaliação atualizada com sucesso!' : 'Avaliação criada com sucesso!');
      
      setLoading(true); 
      const freshReviewsData = await getGameReviews(game.id);
      const transformedFreshReviews = freshReviewsData.map(transformRawReview);
      setReviews(transformedFreshReviews);

      const freshUserReviewFromList = transformedFreshReviews.find(
        (r) => r.user_id === user.id && r.game_id === game.id
      );
      
      if (freshUserReviewFromList) {
        setUserReview(freshUserReviewFromList);
        setReviewText(freshUserReviewFromList.review_text || '');
        setRating(freshUserReviewFromList.rating || null);
        setEditingReviewText(freshUserReviewFromList.review_text || '');
        setEditingRating(freshUserReviewFromList.rating || null);
      } else {
        if (transformedSavedReview.user_id === user.id && transformedSavedReview.game_id === game.id) {
            setUserReview(transformedSavedReview);
            setReviewText(transformedSavedReview.review_text || '');
            setRating(transformedSavedReview.rating || null);
            setEditingReviewText(transformedSavedReview.review_text || '');
            setEditingRating(transformedSavedReview.rating || null);
        } else {
            setUserReview(null);
            setReviewText('');
            setRating(null);
            setEditingReviewText('');
            setEditingRating(null);
        }
      }
      
      setIsEditing(false);

    } catch (err: any) {
      console.error('Erro ao salvar avaliação:', err);
      setError(err.message || 'Erro ao salvar avaliação.');
      toast.error(`Erro ao salvar: ${err.message}`);
    } finally {
      setActionLoading(false);
      setLoading(false); 
      setIsModeratingText(false);
    }
  };

  const handleDeleteReview = async () => {
    if (!userReview || !user) return;

    setActionLoading(true);
    setError(null);
    setShowDeleteConfirmation(false);

    try {
      await deleteGameReview(userReview.id, user.id);
      setUserReview(null);
      setReviewText('');
      setRating(null);
      setIsEditing(false);
      setReviews(reviews.filter(review => review.id !== userReview.id).map(transformRawReview));
    } catch (err: any) {
      console.error('Erro ao excluir avaliação:', err);
      setError(err.message || 'Erro ao excluir avaliação. Tente novamente mais tarde.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAdminDeleteReview = async (reviewId: string) => {
    if (!isAdmin || !user) return;

    setActionLoadingId(reviewId);
    setError(null);
    setShowAdminDeleteConfirmation(false);

    try {
      await deleteGameReview(reviewId, user.id, true);
      setReviews(reviews.filter(review => review.id !== reviewId).map(transformRawReview));
      
      if (userReview?.id === reviewId) {
        setUserReview(null);
        setReviewText('');
        setIsEditing(false);
        setRating(null);
      }
    } catch (err: any) {
      console.error('Erro ao excluir avaliação (admin):', err);
      setError(err.message || 'Erro ao excluir avaliação. Tente novamente mais tarde.');
    } finally {
      setActionLoading(false);
      setActionLoadingId(null);
    }
  };
  
  const handleViewProfile = (username?: string) => {
    if (!username) return;
    navigate(`/profile/${username}`); 
  };
  
  const handleVoteReview = async (reviewId: string, voteType: 'upvote' | 'downvote') => {
    if (!user?.id) {
      setError('É necessário estar logado para votar em avaliações.');
      return;
    }
    
    try {
      setVoteLoading(true);
      setVoteLoadingId(reviewId);
      setError(null);
      
      const voteResult = await voteOnReview(user.id, reviewId, voteType);
      
      if (voteResult) {
        setReviews(reviews.map(review => {
          if (review.id === reviewId) {
            const currentVoteResult = voteResult as unknown as VoteResultType;
            return {
              ...review,
              upvotes: currentVoteResult.upvotes,
              downvotes: currentVoteResult.downvotes,
              balance: currentVoteResult.balance,
              user_vote: currentVoteResult.user_vote
            };
          }
          return review;
        }));
      } else {
        setError('Não foi possível registrar o voto. Tente novamente.');
      }
    } catch (err: any) {
      console.error('Erro ao votar na avaliação:', err);
      setError(err.message || 'Erro ao votar. Tente novamente mais tarde.');
    } finally {
      setVoteLoading(false);
      setVoteLoadingId(null);
    }
  };

  const handleEditReview = (review: GameReviewWithProfile) => {
    setIsEditing(true);
    setEditingReviewText(review.review_text);
    setEditingRating(review.rating);
  };

  if (loading) {
    return (
      <div className={`flex justify-center items-center min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100'}`}>
        <Loader2 className={`h-12 w-12 animate-spin ${theme === 'dark' ? 'text-primary-400' : 'text-primary-600'}`} />
      </div>
    );
  }

  if (error && !game) {
    return (
      <div className={`flex flex-col justify-center items-center min-h-screen p-4 ${theme === 'dark' ? 'bg-gray-900 text-red-400' : 'bg-gray-100 text-red-600'}`}>
        <AlertTriangle size={48} className="mb-4" />
        <h2 className="text-2xl font-semibold mb-2">Ocorreu um erro</h2>
        <p className="text-center mb-6">{error}</p>
        <Link 
          to="/"
          className={`px-6 py-2 rounded-md font-medium transition-colors ${theme === 'dark' ? 'bg-primary-500 text-white hover:bg-primary-600' : 'bg-primary-600 text-white hover:bg-primary-700'}`}
        >
          Voltar para Home
        </Link>
      </div>
    );
  }

  if (!game) {
    return (
      <div className={`flex flex-col justify-center items-center min-h-screen p-4 ${theme === 'dark' ? 'bg-gray-900 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
        <h2 className="text-2xl font-semibold mb-2">Jogo não encontrado</h2>
        <p className="text-center mb-6">O jogo que você está procurando não existe ou não pôde ser carregado.</p>
        <Link 
          to="/search"
          className={`px-6 py-2 rounded-md font-medium transition-colors ${theme === 'dark' ? 'bg-primary-500 text-white hover:bg-primary-600' : 'bg-primary-600 text-white hover:bg-primary-700'}`}
        >
          Procurar Jogos
        </Link>
      </div>
    );
  }

  return (
    <>
      {game && (
      <SEO 
        title={`Avaliações de ${game.name}`}
        description={`Leia e escreva avaliações para o jogo ${game.name}. Veja o que outros jogadores acharam.`}
        image={game.image_url || game.thumb_url || undefined} 
      />
      )}
      <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900 text-gray-200' : 'bg-gray-50 text-gray-800'}`}>
        {game && (
        <div className="container mx-auto max-w-3xl py-8 px-4">
          <button
            onClick={() => navigate(game.slug ? `/games/titulo/${game.slug}` : (gameId ? `/games/${gameId}` : '/'))}
            className={`flex items-center mb-6 text-sm font-medium rounded-md px-3 py-1.5 transition-colors
                        ${theme === 'dark' 
                          ? 'text-primary-400 hover:bg-gray-700'
                          : 'text-primary-600 hover:bg-primary-100'}`}
          >
            <ChevronLeft size={18} className="mr-1" />
            Voltar para {game.name}
          </button>

          <div className={`mb-8 p-5 rounded-lg shadow-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center mb-4">
              {game.thumb_url && (
                <img src={game.thumb_url} alt={game.name} className="w-16 h-16 rounded-md mr-4 shadow-sm" />
              )}
              <div>
                <h1 className={`text-2xl sm:text-3xl font-bold ${theme === 'dark' ? 'text-gray-50' : 'text-gray-900'}`}>{`Avaliações de ${game.name}`}</h1>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Veja o que outros jogadores acharam e compartilhe sua opinião.</p>
              </div>
            </div>
          </div>
          
          {error && (
            <div className={`mb-6 p-4 rounded-md border ${theme === 'dark' ? 'bg-red-900/30 border-red-700 text-red-300' : 'bg-red-100 border-red-400 text-red-700'}`}>
              <div className="flex items-center">
                <AlertTriangle size={20} className="mr-2" />
                <p className="text-sm font-medium">{error}</p>
              </div>
            </div>
          )}

          {user && (
            <div className={`mb-8 p-5 rounded-lg shadow-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
              <h2 className={`text-xl font-semibold mb-4 ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
                {userReview && isEditing ? 'Editar sua avaliação' : (userReview ? 'Sua avaliação' : 'Deixe sua avaliação')}
              </h2>
              {userReview && !isEditing ? (
                <div className={`${theme === 'dark' ? 'bg-gray-750' : 'bg-gray-100'} p-4 rounded-md`}>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`h-5 w-5 ${(rating || 0) > i ? (theme === 'dark' ? 'text-yellow-400' : 'text-yellow-500') : (theme === 'dark' ? 'text-gray-600' : 'text-gray-400')}`}
                          fill={(rating || 0) > i ? 'currentColor' : 'none'}
                        />
                      ))}
                      <span className={`ml-2 text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>({(rating !== null && rating !== undefined) ? rating.toFixed(1) : 'N/A'})</span>
                    </div>
                    <button
                      onClick={() => {
                        setIsEditing(true);
                        setEditingReviewText(reviewText);
                        setEditingRating(rating);
                      }}
                      className={`text-xs px-2.5 py-1 rounded-md flex items-center transition-colors ${theme === 'dark' ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                    >
                      <Edit size={12} className="mr-1" /> Editar
                    </button>
                  </div>
                  <p className={`whitespace-pre-wrap text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{reviewText}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmitReview} className={`p-4 md:p-6 rounded-lg shadow-md ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                  <h2 className={`text-xl font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
                    {isEditing && userReview ? 'Editar sua Avaliação' : 'Deixe sua Avaliação'}
                  </h2>
                  
                  <div className="mb-4">
                    <label htmlFor="rating" className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Nota para o Jogo (de 1 a 5 estrelas):
                    </label>
                    <div className="flex space-x-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          type="button"
                          key={star}
                          onClick={() => setEditingRating(star)}
                          className={`p-2 rounded-full transition-colors 
                                      ${editingRating && editingRating >= star ? (theme === 'dark' ? 'text-yellow-400' : 'text-yellow-500') : (theme === 'dark' ? 'text-gray-600 hover:text-yellow-400' : 'text-gray-300 hover:text-yellow-500')}
                                      ${actionLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          disabled={actionLoading}
                          aria-label={`Avaliar com ${star} estrelas`}
                        >
                          <Star className="h-7 w-7" />
                        </button>
                      ))}
                    </div>
                  </div>

                  <Textarea
                    placeholder="Escreva sua avaliação sobre o jogo aqui..."
                    value={editingReviewText}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditingReviewText(e.target.value)}
                    rows={5}
                    className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-primary-500 
                                ${theme === 'dark' 
                                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-primary-500' 
                                  : 'border-gray-300 placeholder-gray-400 focus:border-primary-500'}`}
                    disabled={actionLoading}
                  />
                  <div className="flex flex-col sm:flex-row items-center justify-end mt-4 space-y-2 sm:space-y-0 sm:space-x-3">
                    {isEditing && userReview && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setIsEditing(false);
                          setEditingReviewText(userReview.review_text || '');
                          setEditingRating(userReview.rating || null);
                        }}
                        disabled={actionLoading || isModeratingText}
                        className={`w-full sm:w-auto ${theme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : ''}`}
                      >
                        Cancelar Edição
                      </Button>
                    )}
                    <Button 
                      type="submit" 
                      disabled={actionLoading || isModeratingText || !editingReviewText.trim() || editingRating === null}
                      className={`w-full sm:w-auto ${theme === 'dark' ? 'bg-primary-600 hover:bg-primary-700 text-white' : 'bg-primary-600 hover:bg-primary-700 text-white'}
                                  ${(actionLoading || isModeratingText || !editingReviewText.trim() || editingRating === null) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {actionLoading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : isModeratingText ? (
                        <ShieldAlert className="mr-2 h-4 w-4 animate-pulse" />
                      ) : (
                        isEditing && userReview ? <Pencil className="mr-2 h-4 w-4" /> : <Send className="mr-2 h-4 w-4" />
                      )}
                      {isEditing && userReview ? 'Salvar Alterações' : 
                       isModeratingText ? 'Analisando Texto...' : 'Publicar Avaliação'}
                    </Button>
                  </div>
                </form>
              )}
              {userReview && isEditing && (
                 <button
                  onClick={() => setShowDeleteConfirmation(true)}
                  disabled={actionLoading || isModeratingText}
                  className={`mt-4 w-full sm:w-auto text-xs px-3 py-1.5 rounded-md flex items-center justify-center transition-colors ${theme === 'dark' ? 'text-red-400 hover:bg-red-500/10' : 'text-red-600 hover:bg-red-100'}`}
                >
                  <Trash2 size={12} className="mr-1" /> Excluir minha avaliação
                </button>
              )}
            </div>
          )}

          <div className={`p-1 md:p-0 rounded-lg ${reviews.length > 0 ? (theme === 'dark' ? '' : '') : ''}`}>
            <h2 className={`text-xl font-semibold mb-5 ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
              {reviews.length > 0 ? `O que outros jogadores dizem (${reviews.length})` : 'Nenhuma avaliação ainda'}
            </h2>
            {reviews.length > 0 ? (
              <div className="space-y-5">
                {reviews.map((review) => (
                  <div key={review.id} className={`p-4 rounded-lg shadow-md relative ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border'}`}>
                    <div className="flex items-start sm:items-center mb-3">
                      <img 
                        src={review.profiles?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(review.profiles?.username || 'Usuário')}&background=random${theme === 'dark' ? '&color=fff&background=374151' : '&color=fff&background=4f46e5'}`}
                        alt={review.profiles?.username || 'Usuário Anônimo'}
                        className="w-10 h-10 sm:w-11 sm:h-11 rounded-full mr-3 sm:mr-4 cursor-pointer flex-shrink-0 shadow-sm"
                        onClick={() => review.profiles?.username && handleViewProfile(review.profiles.username)}
                      />
                      <div className="flex-grow">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                          <p 
                            className={`font-semibold cursor-pointer ${theme === 'dark' ? 'text-gray-100 hover:text-indigo-300' : 'text-gray-800 hover:text-indigo-600'}`}
                            onClick={() => review.profiles?.username && handleViewProfile(review.profiles.username)}
                          >
                            {review.profiles?.username || 'Usuário Anônimo'}
                          </p>
                          <span className={`text-xs mt-0.5 sm:mt-0 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                            {new Date(review.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                        <div className={`flex items-center space-x-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          <div className="flex items-center space-x-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`h-4 w-4 ${review.rating && review.rating >= star ? (theme === 'dark' ? 'text-yellow-400' : 'text-yellow-500') : (theme === 'dark' ? 'text-gray-600' : 'text-gray-300')}`}
                              />
                            ))}
                            <span className={`ml-1 text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                              ({review.rating || 'N/A'})
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <p className={`whitespace-pre-wrap leading-relaxed text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      {review.review_text}
                    </p>

                    <div className="mt-4 pt-3 border-t flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => handleVoteReview(review.id, 'upvote')}
                          disabled={voteLoading && voteLoadingId === review.id}
                          className={`p-1.5 rounded-full flex items-center transition-colors text-xs 
                                      ${review.user_vote === 'upvote' ? (theme === 'dark' ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600') : (theme === 'dark' ? 'text-gray-400 hover:text-green-400 hover:bg-green-500/10' : 'text-gray-500 hover:text-green-600 hover:bg-green-100')}`}
                        >
                          {voteLoading && voteLoadingId === review.id && review.user_vote !== 'upvote' ? <Loader2 size={12} className="animate-spin mr-1" /> : <ThumbsUp size={14} className="mr-1" />}
                          {review.upvotes || 0}
                        </button>
                        <button 
                          onClick={() => handleVoteReview(review.id, 'downvote')}
                          disabled={voteLoading && voteLoadingId === review.id}
                          className={`p-1.5 rounded-full flex items-center transition-colors text-xs 
                                      ${review.user_vote === 'downvote' ? (theme === 'dark' ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600') : (theme === 'dark' ? 'text-gray-400 hover:text-red-400 hover:bg-red-500/10' : 'text-gray-500 hover:text-red-600 hover:bg-red-100')}`}
                        >
                          {voteLoading && voteLoadingId === review.id && review.user_vote !== 'downvote' ? <Loader2 size={12} className="animate-spin mr-1" /> : <ThumbsDown size={14} className="mr-1" />}
                          {review.downvotes || 0}
                        </button>
                        <span className={`text-xs font-medium ${ (review.balance || 0) > 0 ? (theme === 'dark' ? 'text-green-400' : 'text-green-600') : (review.balance || 0) < 0 ? (theme === 'dark' ? 'text-red-400' : 'text-red-500') : (theme === 'dark' ? 'text-gray-400' : 'text-gray-500') }`}>
                          Saldo: {review.balance || 0}
                        </span>
                      </div>
                      {user?.id === review.user_id && !isEditing && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditReview(review)}
                          className={`ml-auto text-xs ${theme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : ''}`}
                        >
                          <Pencil className="mr-1.5 h-3 w-3" />
                          Editar
                        </Button>
                      )}
                      {user?.is_admin && user?.id !== review.user_id && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            setAdminReviewToDeleteId(review.id);
                            setShowAdminDeleteConfirmation(true);
                          }}
                          className="ml-auto text-xs"
                        >
                          <Trash2 className="mr-1.5 h-3 w-3" />
                          Excluir (Admin)
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`text-center py-8 px-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow'}`}>
                <MessageSquare size={40} className={`mx-auto mb-3 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
                <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Ainda não há avaliações para este jogo.</p>
                {user && (
                  <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>Seja o primeiro a compartilhar sua opinião!</p>
                )}
              </div>
            )}
          </div>
        </div>
        )}
        
        {showDeleteConfirmation && (
          <Dialog open={showDeleteConfirmation} onOpenChange={setShowDeleteConfirmation}>
            <DialogContent className={`sm:max-w-md ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : ''}`}>
              <DialogHeader>
                <DialogTitle className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>Confirmar Exclusão</DialogTitle>
              </DialogHeader>
              <DialogDescription className={`mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {reviewToDelete?.id === userReview?.id 
                  ? "Você tem certeza que deseja excluir sua avaliação?" 
                  : `Você tem certeza que deseja excluir a avaliação de ${reviewToDelete?.profiles?.username || 'este usuário'}?`}
                <br />
                Esta ação não pode ser desfeita.
              </DialogDescription>
              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => setShowDeleteConfirmation(false)} className={`${theme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : ''}`}>
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteReview}
                  disabled={actionLoading && actionLoadingId === reviewToDelete?.id}
                >
                  {actionLoading && actionLoadingId === reviewToDelete?.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                  Excluir
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {showAdminDeleteConfirmation && adminReviewToDeleteId && (
          <Dialog open={showAdminDeleteConfirmation} onOpenChange={setShowAdminDeleteConfirmation}>
            <DialogContent className={`sm:max-w-md ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : ''}`}>
              <DialogHeader>
                <DialogTitle className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>Confirmar Exclusão (Admin)</DialogTitle>
              </DialogHeader>
              <DialogDescription className={`mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Você tem certeza que deseja excluir esta avaliação como administrador? Esta ação não pode ser desfeita.
              </DialogDescription>
              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => setShowAdminDeleteConfirmation(false)} className={`${theme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : ''}`}>
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    if (adminReviewToDeleteId) {
                       handleAdminDeleteReview(adminReviewToDeleteId);
                    }
                    setReviewToDelete(null);
                    setAdminReviewToDeleteId(null);
                  }}
                  disabled={actionLoading && actionLoadingId === adminReviewToDeleteId}
                >
                  {actionLoading && actionLoadingId === adminReviewToDeleteId ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                  Excluir como Admin
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </>
  );
};

export default GameReviewsPage; 